import {
  Box,
  Typography,
  Modal,
  CircularProgress,
  Alert,
  FormControl,
  TextField,
  Button,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormLabel,
  Chip,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import CachedIcon from '@mui/icons-material/Cached'
import { AnsweredQuestion, RegeneratedAnswer } from '@/models/Applications'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useApplications } from '@/utils/hooks/applications'

import { UUID } from 'crypto'
import { AccessibleTooltip } from './AccessibleTooltip'
import SpeechToText from './SpeechToText'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'
import { useProfileRegenGuard } from '@/components/ProfileRegenBlockProvider'
import { isAnswerValidationError } from '@/api/answerValidationError'
import {
  OTHER_PREFIX,
  splitMultiChoiceAnswer,
  buildMultiChoiceAnswer,
} from '@/utils/multiChoiceAnswer'

// Stable placeholder so the memo below returns the same reference on every
// render for question types that have no selections to parse.
const NO_MULTI_CHOICE_SELECTION: ReturnType<typeof splitMultiChoiceAnswer> = {
  selections: [],
  otherText: null,
}

const OPERATOR_LABELS: Record<string, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  any_of: 'is any of',
}

interface QuestionAnswerProps {
  data: AnsweredQuestion
  index: number
  token: string
  applicationId: number
  orgId: UUID
  setEmailSent: (emailSent: boolean) => void
  readOnly?: boolean
  isLastInSection?: boolean
  regenState?: 'skipped' | 'failed'
}

export default function QuestionAnswer({
  data,
  index,
  token,
  applicationId,
  orgId,
  setEmailSent,
  readOnly,
  isLastInSection,
  regenState,
}: QuestionAnswerProps) {
  const { canWrite } = usePausedOrg()

  const [showAnswer, setShowAnswer] = useState(false)
  const [regenAnswer, setRegenAnswer] = useState(false)
  const [answerFeedback, setAnswerFeedback] = useState('')
  const [newAnswer, setNewAnswer] = useState<RegeneratedAnswer | null>(null)
  const [generating, setGenerating] = useState(false)
  const [editAnswer, setEditAnswer] = useState(false)
  const [editedAnswer, setEditedAnswer] = useState<string>(
    data.answer.replace(/INSUFFICIENT_INFORMATION/gi, ''),
  )
  const [charCount, setCharCount] = useState(0)
  const [wordCount, setWordCount] = useState(0)
  const [otherText, setOtherText] = useState('')
  const [showOtherField, setShowOtherField] = useState(false)

  const [showMoreIndicator, setShowMoreIndicator] = useState(false)
  const scrollRef = useRef(null)

  // Parsed once per render rather than once per option: the parse walks the
  // option list to keep comma-containing labels whole, so calling it inside the
  // option map costs the square of the option count. Only multi_choice answers
  // are parsed at all — every other type would be re-split on each keystroke
  // for a result nothing reads.
  const isMultiChoice = data.question_metadata?.question_type === 'multi_choice'
  const multiChoiceSelection = useMemo(
    () =>
      isMultiChoice
        ? splitMultiChoiceAnswer(
            editedAnswer,
            data.question_metadata?.options ?? [],
          )
        : NO_MULTI_CHOICE_SELECTION,
    [isMultiChoice, editedAnswer, data.question_metadata?.options],
  )

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const checkForScrollableContent = () => {
    if (scrollRef.current) {
      const { scrollHeight, clientHeight, scrollTop } = scrollRef.current
      const hasScrollableContent = scrollHeight > clientHeight
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5
      setShowMoreIndicator(hasScrollableContent && !isAtBottom)
    }
  }

  useEffect(() => {
    checkForScrollableContent()
  }, [data, newAnswer])

  const handleScroll = () => {
    checkForScrollableContent()
  }

  useEffect(() => {
    const cleanAnswer = data.answer
      .replace(/INSUFFICIENT_INFORMATION/gi, '')
      .trim()

    // for yes_no questions, normalize to match RadioGroup values
    if (data.question_metadata?.question_type === 'yes_no') {
      const normalized = cleanAnswer.toLowerCase()
      if (normalized === 'yes') {
        setEditedAnswer('Yes')
      } else if (normalized === 'no') {
        setEditedAnswer('No')
      } else {
        setEditedAnswer(cleanAnswer)
      }
    }
    // for single_choice, try to match with available options (case-insensitive)
    else if (
      data.question_metadata?.question_type === 'single_choice' &&
      data.question_metadata.options
    ) {
      const matchedOption = data.question_metadata.options.find(
        (opt) => opt.toLowerCase() === cleanAnswer.toLowerCase(),
      )
      setEditedAnswer(matchedOption || cleanAnswer)
    }
    // for multi_choice, normalize separators to comma-space
    else if (
      data.question_metadata?.question_type === 'multi_choice' &&
      data.question_metadata.options
    ) {
      // parse the answer, keeping any "Other: <text>" chunk whole and any
      // comma-containing option label intact
      const { selections, otherText } = splitMultiChoiceAnswer(
        cleanAnswer,
        data.question_metadata.options,
      )

      // try to match each selection with available options (case-insensitive)
      const matchedSelections = selections.map((sel) => {
        const match = data.question_metadata?.options?.find(
          (opt) => opt.toLowerCase() === sel.toLowerCase(),
        )
        return match || sel
      })

      // preserve the Other selection: free text when present, otherwise a bare
      // "Other" if it was selected without any text yet. A question that takes
      // no free text renders no field for it, so carrying a chunk forward would
      // leave the user unable to see or remove text the server then rejects —
      // but the selection itself is kept when "Other" is one of the options,
      // since dropping it would silently discard a choice the user made.
      const otherIsAnOption = data.question_metadata.options.some(
        (opt) => opt.toLowerCase() === 'other',
      )
      const otherSelected =
        otherText !== null ||
        matchedSelections.some((sel) => sel.toLowerCase() === 'other')
      const effectiveOther =
        otherText !== null && data.question_metadata.has_other_text_field
          ? otherText
          : otherSelected && otherIsAnOption
            ? ''
            : null
      setEditedAnswer(buildMultiChoiceAnswer(matchedSelections, effectiveOther))
    } else {
      setEditedAnswer(cleanAnswer)
    }

    // init "Other" text field state
    if (data.question_metadata?.has_other_text_field) {
      if (data.question_metadata.question_type === 'single_choice') {
        if (cleanAnswer.startsWith('Other: ')) {
          setOtherText(cleanAnswer.substring(7))
          setShowOtherField(true)
          setEditedAnswer('Other')
        } else if (cleanAnswer === 'Other') {
          // "Other" is selected but no custom text yet
          setShowOtherField(true)
          setOtherText('')
        }
      } else if (data.question_metadata.question_type === 'multi_choice') {
        const { selections, otherText } = splitMultiChoiceAnswer(
          cleanAnswer,
          data.question_metadata.options ?? [],
        )
        if (otherText !== null) {
          setOtherText(otherText)
          setShowOtherField(true)
        } else if (selections.includes('Other')) {
          // "Other" is selected but no custom text yet
          setShowOtherField(true)
          setOtherText('')
        }
      }
    }

    // initialise character and word counts
    setCharCount(cleanAnswer.length)
    setWordCount(cleanAnswer.split(/\s+/).filter(Boolean).length)
  }, [editAnswer, data.answer, data.question_metadata])

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Whether trying the same thing again could work. A rejected answer will be
  // rejected identically, so telling the user to retry is misleading.
  const [errorRetryable, setErrorRetryable] = useState(true)

  const showError = (message: string, retryable = true) => {
    setError(message)
    setErrorRetryable(retryable)
  }

  const guardRegen = useProfileRegenGuard()
  const { regenerateAnswer, saveAnswer } = useApplications(
    token,
    orgId,
    null,
    applicationId,
  )

  function handleRegenerateAnswer(answerFeedback: string) {
    if (!token) return

    try {
      setError(null)
      setGenerating(true)
      const submittedFeedback = answerFeedback
      regenerateAnswer.mutate(
        {
          token,
          applicationId,
          answerIndex: index,
          answerFeedback,
          regeneratedAnswer:
            newAnswer && newAnswer?.newAnswer !== ''
              ? newAnswer.newAnswer
              : null,
        },
        {
          onSuccess: (data) => {
            setGenerating(false)
            setAnswerFeedback('')
            setNewAnswer({
              ...data,
              regenerationFeedback: submittedFeedback,
            })
            setRegenAnswer(true) // open the model to show the regenerated answer if its a new question
          },
          onError: (err) => {
            setGenerating(false)
            // A profile-regen 409 opens the shared block dialog (updating / retry)
            // instead of a generic failure.
            if (guardRegen(err)) return
            showError('Failed to regenerate answer')
          },
        },
      )
    } catch {
      setGenerating(false)
      showError('Failed to regenerate answer')
    }
  }

  function handleSaveAnswer() {
    if (!token) return

    if (!editedAnswer && editAnswer) {
      return
    }

    if (!newAnswer && !editAnswer) {
      return
    }

    setSaving(true)

    // editedAnswer already contains "Other: text" format from real-time updates
    const finalAnswer = editedAnswer

    // format multi-choice answers with bullet points to match llm format
    let formattedAnswer = finalAnswer
    if (
      editAnswer &&
      data.question_metadata?.question_type === 'multi_choice' &&
      finalAnswer.includes(',')
    ) {
      // convert comma-separated to bullet-point format, keeping the free
      // "Other" text as a single bullet even when it contains commas
      const { selections, otherText } = splitMultiChoiceAnswer(
        finalAnswer,
        data.question_metadata?.options ?? [],
      )
      const bullets = selections.map((s) => `- ${s}`)
      if (otherText !== null) {
        bullets.push(`- ${otherText ? `${OTHER_PREFIX}${otherText}` : 'Other'}`)
      }
      formattedAnswer = bullets.join('\n')
    }

    const answerToSave = editAnswer
      ? {
          newAnswer: formattedAnswer,
          newReasoning: 'No reasoning - answer has been edited',
          newFollowUp: 'No follow-up - answer has been edited',
        }
      : newAnswer!

    try {
      saveAnswer.mutate(
        {
          token,
          applicationId,
          answerIndex: index,
          newAnswer: answerToSave,
        },
        {
          onSuccess: () => {
            handleCloseRegen()
            handleCloseEdit()
            setSaving(false)
            setError(null)
            setEmailSent(false)
            setEditedAnswer(answerToSave.newAnswer)
          },
          onError: (err) => {
            setSaving(false)
            showError(
              err instanceof Error && err.message
                ? err.message
                : 'Failed to save answer',
              !isAnswerValidationError(err),
            )
          },
        },
      )
    } catch {
      showError('Failed to save answer')
    }
  }

  function handleCloseRegen() {
    setRegenAnswer(false)
    setAnswerFeedback('')
    setNewAnswer(null)
    setError(null)
    setGenerating(false)
  }
  function handleCloseEdit() {
    setEditAnswer(false)
    setEditedAnswer(data.answer)
    setError(null)
    setSaving(false)
  }

  function handleRengenerateAnswerClick() {
    // if its a new question, regenerate the answer
    if (data.needs_answer) {
      handleRegenerateAnswer('New question') // 'feedback' for regenerate
    } else {
      setRegenAnswer(true)
    }
  }

  return (
    <>
      {showAnswer && (
        <Modal
          open={showAnswer}
          onClose={() => setShowAnswer(false)}
          aria-labelledby="More Information about this Answer"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: isMobile ? '90%' : '60vw',
              bgcolor: 'background.paper',
              border: '2px solid #000',
              boxShadow: 24,
              px: 6,
              borderRadius: 2,
              pt: 3,
              pb: 4,
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              sx={{ textAlign: 'center' }}
            >
              Information about this Answer
            </Typography>

            <Typography sx={{ fontWeight: '500', mt: 3, mb: 1 }}>
              Reasoning
            </Typography>
            <Typography>{data.reasoning || 'No reasoning given'}</Typography>
            {data.follow_up_comments && (
              <>
                <Typography sx={{ fontWeight: '500', mt: 2 }}>
                  Follow-up
                </Typography>
                <Typography>{data.follow_up_comments}</Typography>
              </>
            )}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setShowAnswer(false)}
                variant="outlined"
                disableRipple
                sx={{
                  borderColor: '#1C3045',
                  border: 2,
                  borderRadius: 2,
                  color: '#1C3045',
                  textTransform: 'none',
                  fontSize: '16px',
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
      {regenAnswer && (
        <Modal
          open={regenAnswer}
          onClose={() => handleCloseRegen()}
          aria-labelledby={
            data.needs_answer ? 'Generated Answer' : 'Regenerate this Answer'
          }
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: isMobile ? '90%' : '60vw',

              bgcolor: 'background.paper',
              border: '2px solid #000',
              boxShadow: 24,
              px: 6,
              borderRadius: 2,
              pt: 3,
              pb: 6,
              maxHeight: '90vh',
              overflowY: 'scroll',
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              sx={{ textAlign: 'center' }}
            >
              {data.needs_answer
                ? 'Generated Answer'
                : 'Regenerate this Answer'}
            </Typography>
            <Box sx={{ position: 'relative', my: 2 }}>
              <Box
                ref={scrollRef}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  position: 'relative',
                  boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                  px: 2,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'transparent',
                    borderRadius: '3px',
                    transition: 'background-color 0.2s ease',
                  },
                  '&:hover::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  },
                }}
                onScroll={handleScroll}
              >
                <Typography sx={{ mt: 2, mb: 1 }}>
                  <span style={{ fontWeight: '500' }}>
                    Application Question:{' '}
                  </span>
                  {data.question_metadata?.question_number || `Q${index + 1}`}:{' '}
                  {data.question || 'No question given'}
                </Typography>

                {/* Show conditional_on info */}
                {data.question_metadata?.conditional_on && (
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', color: '#666', mb: 1 }}
                  >
                    Conditional on: Question{' '}
                    {data.question_metadata.conditional_on.question_number}{' '}
                    {OPERATOR_LABELS[
                      data.question_metadata.conditional_on.operator
                    ] || data.question_metadata.conditional_on.operator}{' '}
                    "{data.question_metadata.conditional_on.value}"
                  </Typography>
                )}

                {/* Show question details */}
                {data.question_metadata?.question_details && (
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', color: '#666', mb: 1 }}
                  >
                    {data.question_metadata.question_details}
                  </Typography>
                )}

                {/* Show Other Text Input option */}
                {data.question_metadata?.has_other_text_field &&
                  data.question_metadata?.options?.includes('Other') && (
                    <Typography
                      variant="caption"
                      sx={{ fontStyle: 'italic', color: '#666', mb: 1 }}
                    >
                      If you choose "Other", please provide details.
                    </Typography>
                  )}

                {/* Show constraints chips */}
                {(data.question_metadata?.char_limit ||
                  data.question_metadata?.word_limit ||
                  data.question_metadata?.required ||
                  data.question_metadata?.attachment_required) && (
                  <Box
                    sx={{ mb: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}
                  >
                    {data.question_metadata.required && (
                      <Chip label="Required" size="small" variant="outlined" />
                    )}
                    {data.question_metadata.attachment_required && (
                      <Chip
                        label="Attachment Required"
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {data.question_metadata.char_limit && (
                      <Chip
                        label={`Max ${data.question_metadata.char_limit} characters`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {data.question_metadata.word_limit && (
                      <Chip
                        label={`Max ${data.question_metadata.word_limit} words`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                )}

                {/* Show options for choice questions */}
                {data.question_metadata?.options &&
                  data.question_metadata.options.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography
                        sx={{ fontSize: '12px', color: '#555', mb: 0.5 }}
                      >
                        {data.question_metadata.question_type === 'multi_choice'
                          ? 'Options (select multiple):'
                          : data.question_metadata.question_type ===
                              'single_choice'
                            ? 'Options (select one):'
                            : 'Options:'}
                      </Typography>
                      {data.question_metadata.options.map((option, idx) => (
                        <Typography
                          key={idx}
                          sx={{ fontSize: '12px', color: '#555', ml: 1 }}
                        >
                          • {option}
                        </Typography>
                      ))}
                    </Box>
                  )}

                {newAnswer && newAnswer.newReasoning ? (
                  <Typography sx={{ mt: 2, mb: 1 }}>
                    <span style={{ fontWeight: '500' }}>
                      {data.needs_answer ? 'Reasoning:' : 'New Reasoning:'}{' '}
                    </span>
                    {newAnswer.newReasoning || 'No reasoning given'}
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      mt: 2,
                      mb: 1,
                      filter: generating ? 'blur(2px)' : 'none',
                      pointerEvents: generating ? 'none' : 'auto',
                      transition: !generating ? 'filter 0.5s ease' : 'none',
                    }}
                  >
                    <span style={{ fontWeight: '500' }}>Reasoning: </span>
                    {data.reasoning || 'No reasoning given'}
                  </Typography>
                )}

                {newAnswer && newAnswer.newAnswer ? (
                  <Typography sx={{ mt: 2, mb: 1 }}>
                    <span style={{ fontWeight: '500' }}>
                      {data.needs_answer ? 'Answer:' : 'New Answer:'}{' '}
                    </span>
                    {newAnswer.newAnswer.replace(
                      /INSUFFICIENT_INFORMATION/gi,
                      'Sorry, there was insufficient information provided to answer this question.',
                    )}
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      mt: 2,
                      mb: 1,
                      filter: generating ? 'blur(2px)' : 'none',
                      pointerEvents: generating ? 'none' : 'auto',
                      transition: !generating ? 'filter 0.5s ease' : 'none',
                    }}
                  >
                    <span style={{ fontWeight: '500' }}>Answer: </span>
                    {data.answer.replace(
                      /INSUFFICIENT_INFORMATION/gi,
                      'Sorry, there was insufficient information provided to answer this question.',
                    )}
                  </Typography>
                )}
                {newAnswer && newAnswer.newFollowUp ? (
                  <Typography sx={{ mt: 2, mb: 1 }}>
                    <span style={{ fontWeight: '500' }}>
                      {data.needs_answer ? 'Follow-up:' : 'New Follow-up:'}{' '}
                    </span>
                    {newAnswer.newFollowUp}
                  </Typography>
                ) : newAnswer && !newAnswer.newFollowUp ? (
                  <Typography sx={{ mt: 2, mb: 1 }}>
                    <span style={{ fontWeight: '500' }}>New Follow-up: </span>
                    No follow-up given
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      mt: 2,
                      mb: 1,
                      filter: generating ? 'blur(2px)' : 'none',
                      pointerEvents: generating ? 'none' : 'auto',
                      transition: !generating ? 'filter 0.5s ease' : 'none',
                    }}
                  >
                    <span style={{ fontWeight: '500' }}>Follow-up: </span>
                    {data.follow_up_comments || 'No follow-up given'}
                  </Typography>
                )}
              </Box>
              {showMoreIndicator && (
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    bottom: '-30px',
                    right: '20px',
                    color: '#1C3045',
                    fontSize: '12px',
                  }}
                >
                  ↓ Scroll for more
                </Typography>
              )}
            </Box>

            <FormControl sx={{ mt: 2, width: '100%' }}>
              <Typography variant="caption">
                Provide details of the changes you would like to make to the
                answer to improve it, then click 'Regenerate Answer'.
              </Typography>
              <Typography variant="caption">
                When you're happy with the answer, click 'Save'. If you want to
                discard the changes, click 'Cancel'.
              </Typography>
              <SpeechToText
                token={token}
                disabled={!canWrite}
                onTranscription={(text) =>
                  setAnswerFeedback((prev) =>
                    prev ? `${prev}\n\n${text}` : text,
                  )
                }
              />
              <TextField
                multiline
                rows={6}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
                value={answerFeedback}
                onChange={(e) => setAnswerFeedback(e.target.value)}
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {error ? (
                  <Alert
                    severity="error"
                    sx={{ mt: 2, justifySelf: 'flex-end' }}
                  >
                    {error}
                    {errorRetryable && ' - Please try again later'}
                  </Alert>
                ) : (
                  <Button
                    sx={{
                      mt: 2,
                      textTransform: 'none',
                      color: '#1C3045',
                      width: '30%',
                      minWidth: '250px',
                      justifyContent: 'center',
                      fontSize: '18px',
                      borderColor: '#1C3045',
                      border: 2,
                      mb: 1,
                      '&:focus': {
                        backgroundColor: '#e8e8e8',
                      },
                    }}
                    variant="outlined"
                    disableRipple
                    disabled={
                      generating ||
                      !answerFeedback.trim() ||
                      error !== null ||
                      saving
                    }
                    onClick={() => handleRegenerateAnswer(answerFeedback)}
                  >
                    {generating ? (
                      <Typography
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: '#1C3045',
                          fontWeight: '500',
                          fontSize: '18px',
                          py: 0.5,
                        }}
                      >
                        <CircularProgress
                          size={20}
                          sx={{ color: '#1C3045', mr: 1 }}
                        />
                        Regenerating...
                      </Typography>
                    ) : (
                      'Regenerate Answer'
                    )}
                  </Button>
                )}
              </Box>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                disableRipple
                sx={{
                  // mt: 2,
                  bgcolor: '#1C3045',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '16px',
                }}
                disabled={saving || error !== null || newAnswer === null}
                onClick={() => handleSaveAnswer()}
              >
                {saving ? (
                  <Typography
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#1C3045',
                      fontWeight: '500',
                      fontSize: '16px',
                      py: 0.5,
                      px: 0.5,
                    }}
                  >
                    <CircularProgress
                      size={20}
                      sx={{ color: '#1C3045', mr: 1 }}
                    />
                    Saving...
                  </Typography>
                ) : (
                  'Save'
                )}
              </Button>

              <Button
                variant="outlined"
                disableRipple
                sx={{
                  // mt: 2,
                  borderColor: '#1C3045',
                  border: 2,
                  borderRadius: 2,
                  color: '#1C3045',
                  textTransform: 'none',
                  fontSize: '16px',
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
                onClick={() => handleCloseRegen()}
                disabled={saving}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
      {editAnswer && (
        <Modal
          open={editAnswer}
          onClose={() => handleCloseEdit()}
          aria-labelledby="Edit this Answer"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: isMobile ? '90%' : '60vw',
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              border: '2px solid #000',
              boxShadow: 24,
              px: 6,
              borderRadius: 2,
              pt: 3,
              pb: 6,
              overflowY: 'auto',
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              sx={{ textAlign: 'center' }}
              id="edit-answer-description"
            >
              Edit Answer
            </Typography>

            <Typography
              variant="body1"
              sx={{ textAlign: 'left', mt: 1, fontWeight: 500 }}
            >
              Application Question:
            </Typography>

            <Typography variant="body1" sx={{ textAlign: 'left', mb: 1 }}>
              {data.question_metadata?.question_number || `Q${index + 1}`}:{' '}
              {data.question}
            </Typography>

            {/* Show conditional_on info */}
            {data.question_metadata?.conditional_on && (
              <Typography
                variant="body2"
                sx={{ fontStyle: 'italic', color: '#666', mb: 1 }}
              >
                Conditional on: Question{' '}
                {data.question_metadata.conditional_on.question_number}{' '}
                {OPERATOR_LABELS[
                  data.question_metadata.conditional_on.operator
                ] || data.question_metadata.conditional_on.operator}{' '}
                "{data.question_metadata.conditional_on.value}"
              </Typography>
            )}

            {/* Show question details */}
            {data.question_metadata?.question_details && (
              <Typography
                variant="body2"
                sx={{ fontStyle: 'italic', color: '#666', mb: 1 }}
              >
                {data.question_metadata.question_details}
              </Typography>
            )}

            {/* Show Other Text Input option */}
            {data.question_metadata?.has_other_text_field &&
              data.question_metadata?.options?.includes('Other') && (
                <Typography
                  variant="caption"
                  sx={{ fontStyle: 'italic', color: '#666', mb: 1 }}
                >
                  If you choose "Other", please provide details.
                </Typography>
              )}

            {/* Show constraints chips */}
            {(data.question_metadata?.char_limit ||
              data.question_metadata?.word_limit ||
              data.question_metadata?.required ||
              data.question_metadata?.attachment_required) && (
              <Box sx={{ mb: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {data.question_metadata.required && (
                  <Chip label="Required" size="small" variant="outlined" />
                )}
                {data.question_metadata.attachment_required && (
                  <Chip
                    label="Attachment Required"
                    size="small"
                    variant="outlined"
                  />
                )}
                {data.question_metadata.char_limit && (
                  <Chip
                    label={`Max ${data.question_metadata.char_limit} characters`}
                    size="small"
                    variant="outlined"
                  />
                )}
                {data.question_metadata.word_limit && (
                  <Chip
                    label={`Max ${data.question_metadata.word_limit} words`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            )}

            <FormControl sx={{ mt: 2, width: '100%' }}>
              {/* Render form component based on question_type */}
              {!data.question_metadata?.question_type ||
              data.question_metadata.question_type === 'textarea' ? (
                <>
                  <Box>
                    <SpeechToText
                      token={token}
                      disabled={!canWrite}
                      onTranscription={(text) => {
                        const newAnswer = editedAnswer
                          ? `${editedAnswer}\n\n${text}`
                          : text
                        setEditedAnswer(newAnswer)
                        setCharCount(newAnswer.length)
                        setWordCount(
                          newAnswer.split(/\s+/).filter(Boolean).length,
                        )
                      }}
                    />
                    <TextField
                      aria-describedby="edit-answer-description"
                      multiline
                      minRows={6}
                      maxRows={20}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#757575',
                        },
                      }}
                      value={editedAnswer}
                      onChange={(e) => {
                        setEditedAnswer(e.target.value)
                        setCharCount(e.target.value.length)
                        setWordCount(
                          e.target.value.split(/\s+/).filter(Boolean).length,
                        )
                      }}
                    />
                  </Box>
                </>
              ) : data.question_metadata.question_type === 'text' ? (
                <TextField
                  aria-describedby="edit-answer-description"
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#757575',
                    },
                  }}
                  value={editedAnswer}
                  onChange={(e) => {
                    setEditedAnswer(e.target.value)
                    setCharCount(e.target.value.length)
                    setWordCount(
                      e.target.value.split(/\s+/).filter(Boolean).length,
                    )
                  }}
                />
              ) : data.question_metadata.question_type === 'single_choice' ? (
                <Box>
                  {data.question_metadata.options && (
                    <FormLabel component="legend" sx={{ mb: 1 }}>
                      Select one option:
                    </FormLabel>
                  )}
                  <Box
                    sx={{
                      maxHeight: '300px',
                      overflowY: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      p: 1,
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      },
                    }}
                  >
                    <RadioGroup
                      value={
                        editedAnswer.startsWith('Other: ')
                          ? 'Other'
                          : editedAnswer
                      }
                      onChange={(e) => {
                        setEditedAnswer(e.target.value)
                        // Show/hide "Other" text field
                        if (
                          data.question_metadata?.has_other_text_field &&
                          e.target.value === 'Other'
                        ) {
                          setShowOtherField(true)
                        } else {
                          setShowOtherField(false)
                          setOtherText('')
                        }
                      }}
                    >
                      {data.question_metadata.options?.map((option, idx) => (
                        <FormControlLabel
                          key={idx}
                          value={option}
                          control={<Radio />}
                          label={option}
                        />
                      ))}
                    </RadioGroup>
                  </Box>

                  {/* "Other" text field for single choice */}
                  {showOtherField &&
                    data.question_metadata?.has_other_text_field && (
                      <TextField
                        multiline
                        rows={4}
                        placeholder="Please specify"
                        value={otherText}
                        onChange={(e) => {
                          setOtherText(e.target.value)
                          // Update editedAnswer with "Other: text" format
                          if (e.target.value.trim()) {
                            setEditedAnswer(`Other: ${e.target.value}`)
                          } else {
                            setEditedAnswer('Other')
                          }
                        }}
                        fullWidth
                        size="small"
                        sx={{ my: 2 }}
                      />
                    )}
                </Box>
              ) : data.question_metadata.question_type === 'multi_choice' ? (
                <Box>
                  {data.question_metadata.options && (
                    <FormLabel component="legend" sx={{ mb: 1 }}>
                      Select all that apply:
                    </FormLabel>
                  )}
                  <Box
                    sx={{
                      maxHeight: '300px',
                      overflowY: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      p: 1,
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      },
                    }}
                  >
                    <FormGroup>
                      {data.question_metadata.options?.map((option, idx) => {
                        const { selections, otherText } = multiChoiceSelection
                        // Check if option is selected, handling "Other: text" format
                        const isChecked =
                          option === 'Other'
                            ? otherText !== null || selections.includes('Other')
                            : selections.includes(option)

                        return (
                          <FormControlLabel
                            key={idx}
                            control={
                              <Checkbox
                                checked={isChecked}
                                onChange={(e) => {
                                  // keep the free "Other" text intact while
                                  // toggling option selections
                                  let nextOther = showOtherField
                                    ? (otherText ?? '')
                                    : null
                                  let nextSelections = selections
                                  if (option === 'Other') {
                                    if (e.target.checked) {
                                      setShowOtherField(true)
                                      nextOther = otherText ?? ''
                                    } else {
                                      setShowOtherField(false)
                                      setOtherText('')
                                      nextOther = null
                                    }
                                  } else if (e.target.checked) {
                                    nextSelections = [...selections, option]
                                  } else {
                                    nextSelections = selections.filter(
                                      (s) => s !== option,
                                    )
                                  }
                                  setEditedAnswer(
                                    buildMultiChoiceAnswer(
                                      nextSelections,
                                      nextOther,
                                    ),
                                  )
                                }}
                              />
                            }
                            label={option}
                          />
                        )
                      })}
                    </FormGroup>
                  </Box>

                  {/* "Other" text field for multi choice */}
                  {showOtherField &&
                    data.question_metadata?.has_other_text_field && (
                      <TextField
                        multiline
                        rows={4}
                        placeholder="Please specify"
                        value={otherText}
                        onChange={(e) => {
                          setOtherText(e.target.value)
                          // Update editedAnswer, keeping the free text whole
                          // (it may contain commas) rather than re-splitting it
                          const { selections } = multiChoiceSelection
                          setEditedAnswer(
                            buildMultiChoiceAnswer(selections, e.target.value),
                          )
                        }}
                        fullWidth
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                </Box>
              ) : data.question_metadata.question_type === 'yes_no' ? (
                <RadioGroup
                  value={editedAnswer}
                  onChange={(e) => setEditedAnswer(e.target.value)}
                >
                  <FormControlLabel
                    value="Yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
              ) : (
                <Box>
                  <SpeechToText
                    token={token}
                    disabled={!canWrite}
                    onTranscription={(text) => {
                      const newAnswer = editedAnswer
                        ? `${editedAnswer}\n\n${text}`
                        : text
                      setEditedAnswer(newAnswer)
                      setCharCount(newAnswer.length)
                      setWordCount(
                        newAnswer.split(/\s+/).filter(Boolean).length,
                      )
                    }}
                  />
                  <TextField
                    aria-describedby="edit-answer-description"
                    multiline
                    minRows={6}
                    maxRows={20}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    }}
                    value={editedAnswer}
                    onChange={(e) => {
                      setEditedAnswer(e.target.value)
                      setCharCount(e.target.value.length)
                      setWordCount(
                        e.target.value.split(/\s+/).filter(Boolean).length,
                      )
                    }}
                  />
                </Box>
              )}

              {/* Show character/word count for text-based inputs */}
              {(data.question_metadata?.char_limit ||
                data.question_metadata?.word_limit) &&
                (data.question_metadata.question_type === 'text' ||
                  data.question_metadata.question_type === 'textarea' ||
                  !data.question_metadata.question_type) && (
                  <Box
                    sx={{
                      mt: 0.5,
                      display: 'flex',
                      gap: 2,
                      justifyContent: 'flex-end',
                    }}
                  >
                    {data.question_metadata.char_limit && (
                      <Typography
                        variant="caption"
                        sx={{
                          color:
                            charCount > data.question_metadata.char_limit
                              ? 'error.main'
                              : 'text.secondary',
                        }}
                      >
                        Characters: {charCount} /{' '}
                        {data.question_metadata.char_limit}
                      </Typography>
                    )}
                    {data.question_metadata.word_limit && (
                      <Typography
                        variant="caption"
                        sx={{
                          color:
                            wordCount > data.question_metadata.word_limit
                              ? 'error.main'
                              : 'text.secondary',
                        }}
                      >
                        Words: {wordCount} / {data.question_metadata.word_limit}
                      </Typography>
                    )}
                  </Box>
                )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {error ? (
                  <Alert
                    severity="error"
                    sx={{ mt: 2, justifySelf: 'flex-end' }}
                  >
                    {error}
                    {errorRetryable && ' - Please try again later'}
                  </Alert>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      variant="contained"
                      disableRipple
                      sx={{
                        mt: 2,
                        bgcolor: '#1C3045',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '16px',
                      }}
                      disabled={
                        saving ||
                        error !== null ||
                        editedAnswer.trim() === data.answer
                      }
                      onClick={() => handleSaveAnswer()}
                    >
                      {saving ? (
                        <Typography
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: '#1C3045',
                            fontWeight: '500',
                            fontSize: '16px',
                            py: 0.5,
                            px: 0.5,
                          }}
                        >
                          <CircularProgress
                            size={20}
                            sx={{ color: '#1C3045', mr: 1 }}
                          />
                          Saving...
                        </Typography>
                      ) : (
                        'Save Changes'
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      disableRipple
                      disabled={saving}
                      sx={{
                        mt: 2,
                        borderColor: '#1C3045',
                        border: 2,
                        borderRadius: 2,
                        color: '#1C3045',
                        textTransform: 'none',
                        fontSize: '16px',
                        '&:focus': {
                          backgroundColor: '#e8e8e8',
                        },
                      }}
                      onClick={() => handleCloseEdit()}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            </FormControl>
          </Box>
        </Modal>
      )}
      <Box
        key={index}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mx: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            mt: 2,
            mb: 2,
            gap: 10,
          }}
        >
          <Box component="header">
            <Typography
              component="h4"
              id={`question-${index}`}
              sx={{
                fontWeight: '500',
                fontSize: '16px',
                margin: 0,
                lineHeight: 'inherit',
              }}
            >
              {data.question_metadata?.question_number || `Q${index + 1}`}:{' '}
              {data.question.split(/\r?\n|\\n/g)[0]}
            </Typography>
            {data.question
              .split(/\r?\n|\\n/g)
              .slice(1)
              .map((line, index) => (
                <Typography
                  key={index}
                  component="p"
                  sx={{
                    fontSize: '14px',
                    fontWeight: 'normal',
                    fontStyle: 'italic',
                    margin: 0,
                    marginTop: '4px',
                  }}
                >
                  {line}
                </Typography>
              ))}

            {regenState && (
              <Chip
                size="small"
                variant="outlined"
                label={
                  regenState === 'skipped'
                    ? 'Needs your input'
                    : 'Generation failed — retry'
                }
                color={regenState === 'skipped' ? 'warning' : 'error'}
                sx={{ mt: 1, borderRadius: 1 }}
                aria-label={
                  regenState === 'skipped'
                    ? `Question ${data.question_metadata?.question_number || `Q${index + 1}`}: needs your input — we didn't have enough information to answer`
                    : `Question ${data.question_metadata?.question_number || `Q${index + 1}`}: generation failed, please retry`
                }
              />
            )}

            {/* Show conditional_on info */}
            {data.question_metadata?.conditional_on && (
              <Typography
                sx={{
                  fontSize: '12px',
                  fontStyle: 'italic',
                  color: '#666',
                  mt: 0.5,
                }}
              >
                Conditional on: Question{' '}
                {data.question_metadata.conditional_on.question_number}{' '}
                {OPERATOR_LABELS[
                  data.question_metadata.conditional_on.operator
                ] || data.question_metadata.conditional_on.operator}{' '}
                "{data.question_metadata.conditional_on.value}"
              </Typography>
            )}

            {/* Show question details */}
            {data.question_metadata?.question_details && (
              <Typography
                sx={{
                  fontSize: '13px',
                  fontStyle: 'italic',
                  color: '#666',
                  mt: 0.5,
                }}
              >
                {data.question_metadata.question_details}
              </Typography>
            )}

            {/* Show Other Text Input option */}
            {data.question_metadata?.has_other_text_field &&
              data.question_metadata?.options?.includes('Other') && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '13px',
                    fontStyle: 'italic',
                    color: '#666',
                    mt: 0.5,
                  }}
                >
                  If you choose "Other", please provide details.
                </Typography>
              )}

            {/* Show options for choice questions */}
            {data.question_metadata?.options &&
              data.question_metadata.options.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ fontSize: '12px', color: '#555', mb: 0.5 }}>
                    {data.question_metadata.question_type === 'multi_choice'
                      ? 'Options (select multiple):'
                      : data.question_metadata.question_type === 'single_choice'
                        ? 'Options (select one):'
                        : 'Options:'}
                  </Typography>
                  {data.question_metadata.options.map((option, idx) => (
                    <Typography
                      key={idx}
                      sx={{ fontSize: '12px', color: '#555', ml: 1 }}
                    >
                      • {option}
                    </Typography>
                  ))}
                </Box>
              )}

            {/* Show character/word limits and other constraints */}
            {(data.question_metadata?.char_limit ||
              data.question_metadata?.word_limit ||
              data.question_metadata?.required ||
              data.question_metadata?.attachment_required) && (
              <Box
                sx={{ mt: 0.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}
              >
                {data.question_metadata.required && (
                  <Chip label="Required" size="small" variant="outlined" />
                )}
                {data.question_metadata.attachment_required && (
                  <Chip
                    label="Attachment Required"
                    size="small"
                    variant="outlined"
                  />
                )}
                {data.question_metadata.char_limit && (
                  <Chip
                    label={`Max ${data.question_metadata.char_limit} characters`}
                    size="small"
                    variant="outlined"
                  />
                )}
                {data.question_metadata.word_limit && (
                  <Chip
                    label={`Max ${data.question_metadata.word_limit} words`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mr: 2, height: '100%' }}>
            {data.question_metadata?.question_type !== 'file_upload' && (
              <>
                <AccessibleTooltip title="Edit Answer" arrow>
                  <IconButton
                    onClick={() => setEditAnswer(true)}
                    sx={{
                      color: '#1C3045',
                      fontSize: '22px',
                    }}
                    aria-label="edit answer"
                    disabled={readOnly}
                  >
                    <ModeEditIcon />
                  </IconButton>
                </AccessibleTooltip>

                <AccessibleTooltip
                  title={
                    data.needs_answer ? 'Generate Answer' : 'Regenerate Answer'
                  }
                  arrow
                >
                  <IconButton
                    onClick={() => handleRengenerateAnswerClick()}
                    sx={{
                      color: '#1C3045',
                      fontSize: '22px',
                    }}
                    disabled={generating || readOnly}
                    aria-label={
                      generating && data.needs_answer
                        ? 'generating answer'
                        : !generating && data.needs_answer
                          ? 'generate answer'
                          : generating
                            ? 'regenerating answer'
                            : 'regenerate answer'
                    }
                  >
                    {generating ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <CachedIcon />
                    )}
                  </IconButton>
                </AccessibleTooltip>
              </>
            )}
            {data.question_metadata?.question_type !== 'file_upload' && (
              <AccessibleTooltip title="Information about this answer" arrow>
                <IconButton
                  onClick={() => setShowAnswer(true)}
                  sx={{
                    color: '#1C3045',
                    fontSize: '22px',
                  }}
                  aria-label="information about this answer"
                >
                  <InfoOutlinedIcon />
                </IconButton>
              </AccessibleTooltip>
            )}
          </Box>
        </Box>
        <Box
          component="section"
          sx={{ position: 'relative' }}
          aria-labelledby={`question-${index}`}
        >
          <Typography
            component="div"
            sx={{
              mb: 2,
              whiteSpace: 'pre-wrap',
              fontStyle:
                data.question_metadata?.question_type === 'file_upload'
                  ? 'italic'
                  : undefined,
              color:
                data.question_metadata?.question_type === 'file_upload'
                  ? 'text.secondary'
                  : undefined,
            }}
          >
            {data.question_metadata?.question_type === 'file_upload'
              ? "File Upload Required on the Funder's version of this form."
              : data.answer.replace(
                  /INSUFFICIENT_INFORMATION/gi,
                  'Sorry, there was insufficient information provided to answer this question.',
                )}{' '}
          </Typography>
          {data.needs_answer === true && (
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                fontSize: '14px',
                color: '#1E4620',
                backgroundColor: '#EDF7ED',
                padding: '4px 10px',
                borderRadius: '4px',
                alignItems: 'center',
              }}
            >
              <InfoOutlinedIcon sx={{ mr: 1 }} />

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                }}
              >
                New Question:{' '}
                <span style={{ fontWeight: 400 }}>
                  Please click 'Edit Answer' to provide an answer or click the
                  'Generate Answer' button{' '}
                </span>
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {!isLastInSection && <Divider />}
    </>
  )
}
